import { useMemo } from "react";
import pageStyles from "../SettingsPage.module.css";
import styles from "./ConnectionsTab.module.css";
import { useOptionalAppConfig } from "../../services/studio/AppConfigProvider";
import { useOptionalConnectionRegistry } from "../../services/ConnectionRegistryProvider";
import type { ConnectionRegistry } from "../../services/connection-registry";
import type { ServiceKey } from "../../services/auth/types";
import type { StudioModule } from "../../services/studio/types";
import { useConnectAction } from "../../services/auth/useConnectAction";

type ConnectionStatus = "connected" | "disconnected" | "unknown";

interface ConnectorRow {
  systemName: string;
  displayName: string;
  status: ConnectionStatus;
  /** Backend listed the connector but its credentials endpoint returned no
   *  authConfig — we can't honour a wire `status: connected` because the
   *  add-in has nothing to authenticate with. */
  missingCredentials: boolean;
}

export function ConnectionsTab() {
  const appConfig = useOptionalAppConfig();
  const registry = useOptionalConnectionRegistry();
  const data = appConfig?.data ?? null;
  const status = appConfig?.status ?? "idle";

  const connectorRows = useMemo(() => {
    if (!data) return [];
    return buildConnectorRows(data.modules, registry);
  }, [data, registry]);

  if (status === "loading" || status === "idle") {
    return (
      <div className={pageStyles.section}>
        <h3 className={pageStyles.sectionTitle}>Connected systems</h3>
        <p className={pageStyles.placeholder}>Loading…</p>
      </div>
    );
  }

  if (status === "error" || !data) {
    return (
      <div className={pageStyles.section}>
        <h3 className={pageStyles.sectionTitle}>Connected systems</h3>
        <p className={pageStyles.placeholder}>
          Could not load settings. Pull-to-refresh or try again later.
        </p>
      </div>
    );
  }

  if (connectorRows.length === 0) {
    return (
      <div className={pageStyles.section}>
        <h3 className={pageStyles.sectionTitle}>Connected systems</h3>
        <p className={styles.emptyHint}>
          No connectors are enabled for your persona in this channel.
        </p>
      </div>
    );
  }

  const personaName = data.user.persona?.name ?? data.user.displayName;

  return (
    <div className={pageStyles.section}>
      <h3 className={pageStyles.sectionTitle}>Connected systems</h3>
      <div className={pageStyles.infoBanner}>
        {connectorRows.length}{" "}
        {connectorRows.length === 1 ? "connector" : "connectors"} enabled for{" "}
        <strong>{personaName}</strong>.
      </div>

      <div className={styles.connectorList}>
        {connectorRows.map((c) => (
          <ConnectorCard key={c.systemName} connector={c} />
        ))}
      </div>
    </div>
  );
}

interface ConnectorCardProps {
  readonly connector: ConnectorRow;
}

function ConnectorCard({ connector }: ConnectorCardProps) {
  const connectAction = useConnectAction(
    connector.systemName as ServiceKey,
    `Connect ${connector.displayName}`,
  );
  // When credentials are missing we cannot trust the wire `status: connected`
  // (and we have nothing to authenticate with), so force the toggle off and
  // disabled — the user needs an admin to wire up the Key Vault entry first.
  const liveConnected = connector.missingCredentials
    ? false
    : connectAction?.connected ?? connector.status === "connected";
  // No adapter means there's nothing in the add-in to connect/disconnect
  // through (e.g. an org-wide connector with no per-user auth) — the toggle
  // is disabled and reads "Available" rather than pretending to be live.
  const toggleDisabled = connector.missingCredentials || !connectAction || connectAction.busy;

  const initials = getInitials(connector.displayName);
  const statusText = connectionStatusText(connector, connectAction, liveConnected);

  const handleToggle = () => {
    if (!connectAction) return;
    if (liveConnected) connectAction.disconnect?.();
    else connectAction.onClick?.();
  };

  return (
    <div className={styles.connectorCard}>
      <div className={styles.iconWrap} aria-hidden>
        {initials}
      </div>
      <div className={styles.body}>
        <div className={styles.name}>{connector.displayName}</div>
        <div className={styles.statusText}>{statusText}</div>
      </div>
      <div className={styles.actions}>
        <label className={pageStyles.toggle}>
          <input
            type="checkbox"
            role="switch"
            aria-label={`${liveConnected ? "Disconnect" : "Connect"} ${connector.displayName}`}
            checked={liveConnected}
            disabled={toggleDisabled}
            aria-busy={connectAction?.busy ? "true" : undefined}
            onChange={handleToggle}
          />
          <span className={pageStyles.toggleSlider} />
        </label>
      </div>
      {connector.missingCredentials && (
        <output className={styles.warn}>
          Credentials not configured for this connector. Contact your administrator.
        </output>
      )}
      {connectAction?.error && (
        <p role="alert" className={styles.error}>
          {connectAction.error}
        </p>
      )}
    </div>
  );
}

/**
 * Walks `modules[*].connectors` to build a deduplicated row per
 * `systemName`. The wire `connections[]` array is intentionally NOT
 * consulted here: its status field reports backend availability, not
 * whether the user has authenticated. The toggle's on/off/disabled state is
 * driven entirely by the adapter and the presence of credentials.
 */
function buildConnectorRows(
  modules: StudioModule[],
  registry: ConnectionRegistry | null,
): ConnectorRow[] {
  // Track whether *any* occurrence of a systemName across modules carries an
  // authConfig — a connector shared across modules only counts as having
  // credentials if at least one occurrence resolved them.
  const credsBySystem = new Map<string, boolean>();
  modules.forEach((m) => {
    m.connectors.forEach((conn) => {
      const has = Boolean(conn.authConfig);
      credsBySystem.set(
        conn.systemName,
        (credsBySystem.get(conn.systemName) ?? false) || has,
      );
    });
  });

  const rows = new Map<string, ConnectorRow>();
  // Preserve the order in which connectors first appear in the
  // modules list so the UI follows the backend's intent. A connector shared
  // across modules produces one row — first occurrence wins.
  modules.forEach((m) => {
    m.connectors.forEach((conn) => {
      if (rows.has(conn.systemName)) return;
      const adapter = registry?.get(conn.systemName as ServiceKey);
      const missingCredentials = !(credsBySystem.get(conn.systemName) ?? false);
      let status: ConnectionStatus;
      if (missingCredentials) {
        status = "unknown";
      } else if (adapter) {
        status = adapter.isConnected() ? "connected" : "disconnected";
      } else {
        status = "disconnected";
      }
      rows.set(conn.systemName, {
        systemName: conn.systemName,
        displayName: conn.name,
        status,
        missingCredentials,
      });
    });
  });

  return Array.from(rows.values());
}

/**
 * Plain-text status line under the connector's name — replaces the raw
 * `systemName` slug, which just repeated the title in a different format
 * (e.g. "Engage Workflow Connection" / "engage-workflow-connection"). The
 * toggle itself is the control; this line is purely informational.
 */
function connectionStatusText(
  connector: ConnectorRow,
  connectAction: ReturnType<typeof useConnectAction>,
  liveConnected: boolean,
): string {
  if (connector.missingCredentials) return "Not configured";
  if (!connectAction) return "Available";
  if (connectAction.busy) return "Connecting…";
  return liveConnected ? "Connected" : "Not connected";
}

function getInitials(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    const first = parts[0] ?? "";
    return first.slice(0, 2).toUpperCase();
  }
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
